import { find, get, isNumber } from 'lodash-es';
import { EdmundsAPI } from 'client/data/api/api-client';
import { getMetricsHeaders } from 'client/utils/metrics-header';
import { ClientConfig } from 'client/configuration';
import { objectToQueryString } from 'site-modules/shared/utils/string';
import { recordError } from 'site-modules/shared/utils/car-buying/client-error-handler';
import { isNew } from 'client/site-modules/shared/utils/inventory-utils/is-new';
import { getNumValueOrDefault } from 'client/site-modules/shared/utils/car-buying/calculator-util';
import {
  mapLoanDataToRetailModel,
  mapLeaseDataToRetailModel,
} from 'site-modules/shared/utils/car-buying/map-to-retail-model';
import {
  DEFAULT_LEASE_TERM,
  DEFAULT_TERM,
  DEFAULT_TRADE_IN,
  DEFAULT_LEASE_ANNUAL_MILEAGE,
  DEFAULT_LEASE_DOWN_PAYMENT,
  DEFAULT_LOAN_DOWN_PAYMENT,
} from 'site-modules/shared/constants/calculator/calculator';
import { DEFAULT_CREDIT_SCORE } from 'site-modules/shared/constants/car-buying';
import { getDefaultApiHeaders } from 'site-modules/shared/utils/car-buying/api-helper';
import { getIsNonTMVDealer } from 'site-modules/shared/utils/inventory/vdp-utils';
import {
  filterInPrimaryIncentives,
  filterOutPrimaryIncentives,
} from 'client/site-modules/shared/utils/incentives/incentives-service';
import { getTotalRebates } from 'site-modules/shared/components/inventory/utils/get-total-rebates';
import { ADVERTISED_PRICE_SOURCE } from 'site-modules/shared/constants/inventory/prices';
import { getOffers, getOfferOrFallback } from './digital-retail-payment-service';

export const LEASE_PAYMENT_API_URL = '/calculators/v2/leasepayment';
export const TERMS_API_ERROR_FIELD = 'TermsAPIError';
export const INCENTIVES_API_ERROR_FIELD = 'IncentivesAPIError';
export const CALCULATION_API_ERROR_FIELD = 'CalculationAPIError';
const LOAN_PAYMENT_API_URL = '/calculators/v2/loanpayment';
const LOAN_AGGREGATION_CALCULATE_API_URL = '/car-buying/v1/aggregation/calculate/loanpayment';
const LEASE_AGGREGATION_CALCULATE_API_URL = '/car-buying/v1/aggregation/calculate/leasepayment';

function getMarketPrice(vehicle) {
  const { prices: { tmv } = {}, computedDisplayInfo = {}, type, dealerInfo = {} } = vehicle;
  const isNonTMVDealer = getIsNonTMVDealer(dealerInfo.franchiseId);

  if (isNonTMVDealer) {
    return 0;
  }

  return isNew(type) ? tmv : computedDisplayInfo.priceValidation?.listPriceEstimate;
}

export function getVehiclePricesNewFields(vehicle) {
  const { prices: { dealerMsrp, advertisedPrice, advertisedPriceSource, docFee } = {} } = vehicle;

  return {
    msrp: dealerMsrp,
    dealerPrice: advertisedPriceSource === ADVERTISED_PRICE_SOURCE.INTERNET_PRICE ? advertisedPrice : 0,
    marketPrice: getMarketPrice(vehicle),
    sellingPrice: advertisedPrice,
    docFee: docFee || null,
  };
}

// TODO: should be removed when all places, which consume this function migrate to new ingestion prices
export function getVehiclePrices(vehicle) {
  const { prices: { docFee, displayPrice } = {}, type, computedDisplayInfo = {} } = vehicle;
  const dealerPrice = isNew(type) ? computedDisplayInfo.transparentPricingCompliance?.guaranteedPrice : displayPrice;

  return {
    msrp: displayPrice,
    dealerPrice,
    marketPrice: getMarketPrice(vehicle),
    sellingPrice: isNew(type) ? dealerPrice || displayPrice : displayPrice,
    docFee: docFee || null,
  };
}

const getBenchmarkPrice = ({ vehicle, vehiclePrices, offer, useDealerPrice }) => {
  const { dealerPrice, msrp } = vehiclePrices;

  if (useDealerPrice) {
    return dealerPrice;
  }

  const isNonTmvDealer = getIsNonTMVDealer(get(vehicle, 'dealerInfo.franchiseId'));

  return isNonTmvDealer ? msrp : offer.sellingPrice;
};

export function getLeasePaymentQueryParams(
  {
    vehicle,
    vehiclePrices,
    zipCode,
    currentOffer,
    prices,
    selectedDownPayment,
    selectedTradeIn = DEFAULT_TRADE_IN,
    customVehiclePrice,
    useDealerPrice,
  },
  isMultiPayments = false
) {
  const { vin } = vehicle;
  const styleId = get(vehicle, 'vehicleInfo.styleInfo.styleId');
  const incentives = currentOffer?.incentives ?? [];

  const { term, residual, moneyFactor, annualMileage, acquisitionFee, docFee, securityDeposit = 0 } = currentOffer;
  const { msrp } = vehiclePrices;

  const benchmarkPrice =
    customVehiclePrice || getBenchmarkPrice({ vehicle, vehiclePrices, offer: currentOffer, useDealerPrice });
  const commonParams = {
    term,
    tradeIn: selectedTradeIn,
    downPayment: selectedDownPayment,
    msrp,
    acquisitionFee,
    securityDeposit,
    docFee,
    residualValuePercentage: residual * 100,
    moneyFactor,
    firstMonthCapitalized: false,
    docFeeCapitalized: true,
    acquisitionFeeCapitalized: true,
    vin,
    zipCode,
    styleId,
    withTaxesAndFees: true,
    annualMileage,
    incentivesAmount: getTotalRebates({
      incentives,
      showLeasePayments: true,
    }),
  };

  return isMultiPayments
    ? {
        ...commonParams,
        benchmarkPrices: prices,
      }
    : { ...commonParams, benchmarkPrice };
}

function getLoanPaymentQueryParams(
  {
    vehicle,
    vehiclePrices,
    zipCode,
    selectedDownPayment,
    prices,
    currentOffer,
    selectedTradeIn = DEFAULT_TRADE_IN,
    useDealerPrice,
  },
  isMultiPayments = false
) {
  const { term, apr, incentives } = currentOffer;
  const { docFee, msrp, dealerPrice, marketPrice, sellingPrice } = vehiclePrices;
  const styleId = get(vehicle, 'vehicleInfo.styleInfo.styleId');
  const isNewType = isNew(vehicle.type);

  const marketValue = isNewType ? (useDealerPrice && dealerPrice) || marketPrice || msrp : marketPrice || sellingPrice;

  const calculatorType = isNewType ? 'New' : 'Used';

  const primaryIncentiveAmount = getTotalRebates({
    incentives: filterInPrimaryIncentives(incentives),
    showLeasePayments: false,
  });

  const conditionalIncentiveAmount = getTotalRebates({
    incentives: filterOutPrimaryIncentives(incentives),
    showLeasePayments: false,
  });

  const salesPrice = isNewType ? (useDealerPrice && dealerPrice) || marketPrice || msrp : sellingPrice;

  const commonParams = {
    zipCode,
    styleId,
    marketValue,
    primaryIncentiveAmount,
    conditionalIncentiveAmount,
    docFee: getNumValueOrDefault(docFee),
    numberOfMonths: term,
    downPayment: selectedDownPayment,
    financeRate: apr,
    tradeIn: selectedTradeIn,
    titleFees: 0,
    applyLimits: true,
    calculatorType,
    withTaxesAndFees: !!styleId,
    msrp,
  };

  return isMultiPayments
    ? {
        ...commonParams,
        salesPrices: prices,
      }
    : { ...commonParams, salesPrice };
}

/**
 * Returns lease quote API data with recalculated monthlyPayment
 * @param {Object} data - lease offer
 * @param {string} visitorId
 * @param {string} pageName
 * @param {string} venomVersion
 * @param {boolean} useDealerPrice
 * @returns {Promise}
 */
export async function getLeaseMonthlyPayment(data, { visitorId, pageName, venomVersion, useDealerPrice }) {
  const leaseQuoteParams = getLeasePaymentQueryParams({ ...data, useDealerPrice });
  const zipCode = get(leaseQuoteParams, 'customer.zipCode');
  const queryParams = objectToQueryString({
    ...leaseQuoteParams,
  });
  const url = `${LEASE_PAYMENT_API_URL}?${queryParams}`;
  const options = {
    method: 'GET',
    headers: {
      'content-type': 'application/json',
      ...getDefaultApiHeaders(visitorId),
      ...getMetricsHeaders('getLeaseMonthlyPayment', pageName, venomVersion),
    },
    timeout: ClientConfig.get('carBuying').apiDefaultTimeoutMs,
  };
  let leaseData;
  try {
    leaseData = await EdmundsAPI.fetchJson(url, options);
    leaseData = mapLeaseDataToRetailModel(
      { ...leaseData, fees: { ...leaseData.fees, docFee: leaseQuoteParams.docFee } },
      zipCode
    );
  } catch (error) {
    recordError();
    throw new Error(error);
  }

  return leaseData;
}

async function getLoanMonthlyPayment(data, { visitorId, pageName, venomVersion, useDealerPrice }) {
  const loanQuoteParams = getLoanPaymentQueryParams({ ...data, useDealerPrice });
  const queryParams = objectToQueryString({
    ...loanQuoteParams,
  });

  const docFee = get(data, 'vehicle.prices.docFee', null);

  const url = `${LOAN_PAYMENT_API_URL}/?${queryParams}`;

  let loanData;
  try {
    loanData = await EdmundsAPI.fetchJson(url, {
      headers: {
        ...getDefaultApiHeaders(visitorId),
        ...getMetricsHeaders('getLoanMonthlyPayment', pageName, venomVersion),
      },
    });
    loanData = mapLoanDataToRetailModel({ ...loanData, fees: { ...loanData.fees, docFee } });
  } catch (error) {
    recordError();
    throw new Error(error);
  }

  return loanData;
}

export function getMonthlyPayment(data, { isPurchase, ...pageParams }) {
  return isPurchase ? getLoanMonthlyPayment(data, pageParams) : getLeaseMonthlyPayment(data, pageParams);
}

export function getOfferByMileage({ digitalRetailOffers, selectedMileage, selectedTerm }) {
  const targetMileage = digitalRetailOffers.annualMileage[String(selectedMileage)];

  if (!targetMileage) {
    return null;
  }

  return targetMileage.find(offer => offer.term === selectedTerm) || targetMileage[0];
}

export async function recalculateValues(
  {
    digitalRetailOffers,
    selectedDownPayment,
    selectedMileage,
    selectedTerm,
    vehicle,
    vehiclePrices,
    zipCode,
    selectedTradeIn,
    customVehiclePrice,
  },
  pageParams
) {
  if (!digitalRetailOffers) {
    return null;
  }

  const isPurchase = get(pageParams, 'isPurchase');
  const currentOffer = getOfferByMileage({
    digitalRetailOffers,
    selectedMileage,
    selectedTerm,
  });

  if (!currentOffer) {
    recordError();
    throw new Error('currentOffer is empty');
  }

  const monthlyPaymentParams = {
    vehicle,
    vehiclePrices,
    zipCode,
    currentOffer,
    selectedDownPayment,
    selectedTradeIn,
    customVehiclePrice,
  };

  const monthlyPaymentData = await getMonthlyPayment(monthlyPaymentParams, pageParams);
  const { tradeIn, downPayment } = monthlyPaymentData;
  const downPaymentValue = isNumber(downPayment) ? downPayment : selectedDownPayment;
  return {
    ...currentOffer,
    ...monthlyPaymentData,
    tradeIn: isNumber(tradeIn) ? tradeIn : selectedTradeIn,
    downPayment: downPaymentValue,
    isPurchase,
  };
}

export async function getMultiMonthlyPayments(data, { isPurchase, visitorId, pageName, venomVersion }) {
  const { offer, selectedMileage, selectedTerm, useGraphQlForLoanOffers } = data;
  let currentOffer = getOfferByMileage({
    selectedTerm,
    selectedMileage,
    digitalRetailOffers: offer,
  });

  currentOffer = useGraphQlForLoanOffers
    ? getOfferOrFallback({
        selectedMileage,
        selectedTerm,
        offer: currentOffer,
      })
    : currentOffer;

  if (!currentOffer) {
    recordError();

    return null;
  }

  const monthlyPaymentParams = {
    currentOffer,
    ...data,
  };

  const quoteParams = isPurchase
    ? getLoanPaymentQueryParams(monthlyPaymentParams, true)
    : getLeasePaymentQueryParams(monthlyPaymentParams, true);
  const queryParams = objectToQueryString({
    ...quoteParams,
  });

  const docFee = get(data, 'vehicle.prices.docFee', null);
  const zipCode = get(quoteParams, 'customer.zipCode');

  const options = {
    method: 'GET',
    headers: {
      'content-type': 'application/json',
      ...getDefaultApiHeaders(visitorId),
      ...getMetricsHeaders(isPurchase ? 'getLoanMonthlyPayment' : 'getLeaseMonthlyPayment', pageName, venomVersion),
    },
    timeout: ClientConfig.get('carBuying').apiDefaultTimeoutMs,
  };

  try {
    const results = await EdmundsAPI.fetchJson(
      `${isPurchase ? LOAN_AGGREGATION_CALCULATE_API_URL : LEASE_AGGREGATION_CALCULATE_API_URL}?${queryParams}`,
      options
    );

    return results.map(result => {
      let mappedData;

      if (isPurchase) {
        mappedData = mapLoanDataToRetailModel({ ...result, fees: { ...result.fees, docFee } });
      } else {
        mappedData = mapLeaseDataToRetailModel(
          { ...result, fees: { ...result.fees, docFee: quoteParams.docFee } },
          zipCode
        );
      }

      return {
        ...currentOffer,
        ...mappedData,
        tradeIn: isNumber(mappedData.tradeIn) ? mappedData.tradeIn : data.selectedTradeIn,
        downPayment: isNumber(mappedData.downPayment) ? mappedData.downPayment : data.selectedDownPayment,
      };
    });
  } catch {
    recordError();

    return null;
  }
}

export function getDefaultOffer(digitalRetailOffers, isPurchase = false) {
  const { annualMileage } = digitalRetailOffers;

  const offersByMileage =
    annualMileage[String(DEFAULT_LEASE_ANNUAL_MILEAGE)] ||
    annualMileage[Object.keys(annualMileage).sort().reverse()[0]];

  const defaultTerm = isPurchase ? DEFAULT_TERM : DEFAULT_LEASE_TERM;
  return offersByMileage.filter(offer => offer.term === defaultTerm)[0] || offersByMileage[0];
}

/**
 * Returns initial state for Payments Calculator
 * If calculatorSelections exists and selected term and mileage combination is available we use it as an initial Payments Calculator state
 * Else we use default lease offer term and mileage combination
 * @param {Array} digitalRetailOffers
 * @param {Object} calculatorSelections
 * @param {Number} defaultTerm
 * @param {Number} defaultAnnualMileage
 * @param {Boolean} isPurchase
 * @returns {Object} - term, mileage, downPayment, and tradeIn combination
 */
export function getCalculatorInitialState(
  digitalRetailOffers,
  calculatorSelections,
  { term: defaultTerm, annualMileage: defaultAnnualMileage },
  isPurchase
) {
  const { downPayment, tradeIn } = calculatorSelections || {};
  const defaultDownPayment = downPayment || (isPurchase ? DEFAULT_LOAN_DOWN_PAYMENT : DEFAULT_LEASE_DOWN_PAYMENT);
  const defaultTradeIn = tradeIn || DEFAULT_TRADE_IN;

  const defaultSelections = {
    term: defaultTerm,
    annualMileage: defaultAnnualMileage,
    downPayment: defaultDownPayment,
    tradeIn: defaultTradeIn,
  };
  if (calculatorSelections) {
    const vdpSelections = { ...defaultSelections, ...calculatorSelections };
    const { term, annualMileage } = vdpSelections;
    const offerByMileage = find(digitalRetailOffers.annualMileage[String(annualMileage)], { term });
    if (offerByMileage) return vdpSelections;
    const offerByDefaultMileage = find(digitalRetailOffers.annualMileage[String(defaultAnnualMileage)], { term });
    if (offerByDefaultMileage) return { ...defaultSelections, term };
  }

  return defaultSelections;
}

export async function recalculateOffer(
  { annualMileage, transId, downPayment, term, vehicle, vin, zipCode, filteredIncentives, tradeIn, creditScore },
  pageParams
) {
  const {
    vehicleInfo: {
      styleInfo: { styleId, year },
      mileage,
    },
  } = vehicle;

  const digitalRetailOffers = await getOffers(
    {
      styleId,
      transId,
      creditScore: transId ? undefined : creditScore || DEFAULT_CREDIT_SCORE,
      vin,
      zipCode,
      filteredIncentives,
      year,
      mileage,
    },
    pageParams
  );

  const recalculatedOffer = await recalculateValues(
    {
      digitalRetailOffers,
      selectedDownPayment: downPayment,
      selectedMileage: annualMileage,
      selectedTerm: term,
      vehicle,
      vehiclePrices: getVehiclePrices(vehicle),
      zipCode,
      selectedTradeIn: tradeIn,
    },
    pageParams
  );

  return {
    digitalRetailOffers,
    recalculatedOffer,
  };
}
